'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { ArrowDownCircle } from 'lucide-react';
import { useABTest } from '@/components/ABTestWrapper';

export function HeroSection() {
  // A/B Testing for continuous optimization
  const headlineTest = useABTest('hero-headline-test');
  const ctaTest = useABTest('cta-button-test');
  
  const scrollToAnalyzer = () => {
    // Track A/B test conversion
    headlineTest.trackConversion('scroll_to_analyzer');
    ctaTest.trackConversion('primary_cta_click');
    
    const analyzerSection = document.getElementById('analyzer');
    if (analyzerSection) {
      analyzerSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative w-full min-h-screen bg-gradient-to-br from-cyan-500 via-blue-500 to-primary py-20 sm:py-32 lg:py-40 flex items-center">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-white/5" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px'
        }}></div>
      </div>
      
      <div className="w-full px-6 sm:px-8 lg:px-12 relative z-10">
        <div className="w-full text-center">
          {/* Badge */}
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-medium mb-8">
            <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
            Free AI-Powered Website Audit
          </div>
          
          {/* Main Headline */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-8 font-headline leading-tight">
            {headlineTest.isVariant('variant-a') ? (
              <>
                Convert More Visitors.
                <br className="hidden sm:block" />
                <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
                  Grow Your Business
                </span>
              </>
            ) : (
              <>
                Stop Losing Leads.
                <br className="hidden sm:block" />
                <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
                  Start Converting Visitors
                </span>
              </>
            )}
          </h1>
          
          {/* Subtitle */}
          <p className="text-xl sm:text-2xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
            Your website visitors aren't converting into customers. Our AI audit identifies exactly what's blocking your sales and shows you how to fix it in under 48 hours.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-16">
            <Button 
              size="lg" 
              onClick={scrollToAnalyzer} 
              className="bg-white text-cyan-600 hover:bg-cyan-50 shadow-2xl w-full sm:w-auto px-8 py-4 text-lg font-semibold rounded-full transform hover:scale-105 transition-all duration-200"
            >
              <ArrowDownCircle className="mr-3 h-6 w-6" />
              {ctaTest.isVariant('variant-a') ? 'Get My Free Analysis' : 'See My Lead Report Now'}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white bg-white/10 text-white hover:bg-white hover:text-cyan-600 shadow-2xl w-full sm:w-auto px-8 py-4 text-lg font-semibold rounded-full transform hover:scale-105 transition-all duration-200" 
              onClick={() => (document.getElementsByClassName('calendly-badge-content')[0] as HTMLElement)?.click()}
            >
              Get My Free Audit
            </Button>
          </div>
          
          {/* Trust Indicators */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 text-white/80">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">✓</span>
              </div>
              <span className="text-sm font-medium">100% Free Report</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">⚡</span>
              </div>
              <span className="text-sm font-medium">48-Hour Delivery</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-cyan-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">🎯</span>
              </div>
              <span className="text-sm font-medium">Actionable Insights</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <ArrowDownCircle className="h-8 w-8 text-white/60" />
      </div>
    </section>
  );
}
